/**
 * AES-GCM-256 + AES-KW-256 primitives via SubtleCrypto.
 *
 * Conventions:
 *   - 12-byte IV per encrypt op (random, never reused with same key).
 *   - 16-byte GCM tag appended to ciphertext (SubtleCrypto default).
 *   - AAD binds ciphertext to its row context (item_id || version || scope)
 *     so a server replay of a prior ciphertext fails authentication.
 *
 * No fallback: AES-GCM is required at the runtime layer. If a target
 * platform lacks SubtleCrypto we refuse to run rather than weaken crypto.
 */

import { concatBytes, randomBytes, utf8Encode } from "./bytes.js";
import { asBrand } from "../types/index.js";
import type {
  CiphertextBytes,
  IvBytes,
  RawSymmetricKey,
  VaultItemAadContext,
  WrappedKeyBytes,
} from "../types/index.js";

/** Generate a random AES-GCM IV (12 bytes). */
export function generateIv(): IvBytes {
  return asBrand<IvBytes>(randomBytes(12));
}

/** Generate a fresh random 32-byte symmetric key (for use as CK or ASK). */
export function generateAesKey(): RawSymmetricKey {
  return asBrand<RawSymmetricKey>(randomBytes(32));
}

/** Import raw 32 bytes as a non-extractable AES-GCM CryptoKey. */
export async function importAesGcmKey(
  raw: RawSymmetricKey,
  extractable: boolean = false,
): Promise<CryptoKey> {
  if (raw.length !== 32) {
    throw new RangeError(
      `importAesGcmKey: expected 32 bytes, got ${raw.length}`,
    );
  }
  return crypto.subtle.importKey(
    "raw",
    raw as unknown as ArrayBuffer,
    { name: "AES-GCM" },
    extractable,
    ["encrypt", "decrypt"],
  );
}

/** Import raw 32 bytes as an AES-KW CryptoKey for wrapKey/unwrapKey ops. */
export async function importAesKwKey(
  raw: RawSymmetricKey,
  extractable: boolean = false,
): Promise<CryptoKey> {
  if (raw.length !== 32) {
    throw new RangeError(
      `importAesKwKey: expected 32 bytes, got ${raw.length}`,
    );
  }
  return crypto.subtle.importKey(
    "raw",
    raw as unknown as ArrayBuffer,
    { name: "AES-KW" },
    extractable,
    ["wrapKey", "unwrapKey"],
  );
}

// =============================================================================
// AES-GCM encrypt / decrypt
// =============================================================================

export interface AesGcmEncryptInput {
  readonly key: CryptoKey;
  readonly iv: IvBytes;
  readonly plaintext: Uint8Array;
  readonly aad?: Uint8Array;
}

export async function aesGcmEncrypt(
  input: AesGcmEncryptInput,
): Promise<CiphertextBytes> {
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: input.iv as unknown as ArrayBuffer,
      ...(input.aad !== undefined
        ? { additionalData: input.aad as unknown as ArrayBuffer }
        : {}),
      tagLength: 128,
    },
    input.key,
    input.plaintext as unknown as ArrayBuffer,
  );
  return asBrand<CiphertextBytes>(new Uint8Array(ciphertext));
}

export interface AesGcmDecryptInput {
  readonly key: CryptoKey;
  readonly iv: IvBytes;
  readonly ciphertext: CiphertextBytes;
  readonly aad?: Uint8Array;
}

/**
 * Decrypt AES-GCM ciphertext. Throws on auth failure (any tamper, wrong key,
 * wrong IV, wrong AAD, replayed ciphertext under different context).
 */
export async function aesGcmDecrypt(
  input: AesGcmDecryptInput,
): Promise<Uint8Array> {
  const plaintext = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: input.iv as unknown as ArrayBuffer,
      ...(input.aad !== undefined
        ? { additionalData: input.aad as unknown as ArrayBuffer }
        : {}),
      tagLength: 128,
    },
    input.key,
    input.ciphertext as unknown as ArrayBuffer,
  );
  return new Uint8Array(plaintext);
}

// =============================================================================
// AAD builder for vault items
// =============================================================================

/**
 * Build the canonical AAD for a vault item AES-GCM op.
 *
 * Format: utf8("<item_id>:<version>:<scope>") - human-readable on purpose so
 * debugging a decrypt failure is straightforward.
 *
 * Bound elements:
 *   - item_id: prevents cross-item swap (ct from item A pasted onto row B).
 *   - version: prevents server rollback (replay v(n-1) ciphertext as v(n)).
 *   - scope:   prevents scope confusion (force a company decrypt as personal).
 */
export function buildVaultItemAad(ctx: VaultItemAadContext): Uint8Array {
  if (!Number.isInteger(ctx.version) || ctx.version < 1) {
    throw new RangeError(`buildVaultItemAad: invalid version ${ctx.version}`);
  }
  return utf8Encode(`${ctx.item_id}:${ctx.version}:${ctx.scope}`);
}

// =============================================================================
// AES-KW wrap / unwrap (used by ECDH envelope)
// =============================================================================

/**
 * Wrap raw 32-byte symmetric key material using AES-KW-256.
 *
 * The wrappingKey is imported as AES-KW; the keyToWrap is imported as
 * AES-GCM (extractable) and re-exported via wrapKey. SubtleCrypto AES-KW
 * produces a 40-byte output for 32-byte input.
 */
export async function aesKwWrap(
  wrappingKey: CryptoKey,
  keyToWrap: RawSymmetricKey,
): Promise<WrappedKeyBytes> {
  if (keyToWrap.length !== 32) {
    throw new RangeError(`aesKwWrap: expected 32 bytes, got ${keyToWrap.length}`);
  }
  const importedToWrap = await crypto.subtle.importKey(
    "raw",
    keyToWrap as unknown as ArrayBuffer,
    { name: "AES-GCM" },
    true, // extractable: required for wrap
    ["encrypt", "decrypt"],
  );
  const wrapped = await crypto.subtle.wrapKey(
    "raw",
    importedToWrap,
    wrappingKey,
    { name: "AES-KW" },
  );
  return asBrand<WrappedKeyBytes>(new Uint8Array(wrapped));
}

/**
 * Unwrap a 40-byte AES-KW blob back to raw 32-byte key material.
 *
 * SubtleCrypto.unwrapKey only returns a CryptoKey, so we round-trip through
 * an extractable raw export to get the bytes. Caller can then re-import the
 * raw key into the algorithm of their choice (AES-GCM, HKDF, AES-KW...).
 */
export async function aesKwUnwrap(
  unwrappingKey: CryptoKey,
  wrapped: WrappedKeyBytes,
): Promise<RawSymmetricKey> {
  if (wrapped.length !== 40) {
    throw new RangeError(
      `aesKwUnwrap: expected 40-byte wrapped blob, got ${wrapped.length}`,
    );
  }
  const unwrappedKey = await crypto.subtle.unwrapKey(
    "raw",
    wrapped as unknown as ArrayBuffer,
    unwrappingKey,
    { name: "AES-KW" },
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
  const raw = await crypto.subtle.exportKey("raw", unwrappedKey);
  const u8 = new Uint8Array(raw);
  if (u8.length !== 32) {
    throw new RangeError(`aesKwUnwrap: unwrapped to ${u8.length} bytes`);
  }
  return asBrand<RawSymmetricKey>(u8);
}

// =============================================================================
// Iv + ct concatenation helpers
// =============================================================================

/**
 * Concatenate IV (12 bytes) + ciphertext into a single blob suitable for
 * single-column storage. Used for wrapped_priv and wrapped_ask and totp_secret_ct.
 */
export function concatIvCt(iv: IvBytes, ct: CiphertextBytes): Uint8Array {
  return concatBytes(iv, ct);
}

/** Split a stored IV||ciphertext blob back to (iv, ct). */
export function splitIvCt(
  blob: Uint8Array,
): { iv: IvBytes; ciphertext: CiphertextBytes } {
  if (blob.length < 12 + 16) {
    throw new RangeError(`splitIvCt: blob too short (${blob.length} bytes)`);
  }
  return {
    iv: asBrand<IvBytes>(blob.slice(0, 12)),
    ciphertext: asBrand<CiphertextBytes>(blob.slice(12)),
  };
}
